/**
 * VisualizationControls.tsx — Left-side element list grouped by type
 *
 * Matches Figma design: transparent/glass panel, grouped by type,
 * badge-style FOR/FOV toggles, per-element hover icons (crosshair, eye),
 * orbit/axes toggle icon. Selected row has subtle green highlight.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import useGenericSimulator from '../../../hooks/useGenericSimulator';
import useUrlParams from '../../../hooks/useUrlParams';
import { ElementInfo, ElementType, SelectedElementInfo } from '../../../types';
import { truncateName } from '../../../../../utils/commonFunctions';
import { computeAltitudeKm, computeSpeedMs, computeLVLHEulerAngles, getEcefHistorySnapshot, isViewerReady } from '../../../helpers/cesiumPlotHelpers';
import CustomToolTip from '../../../../CommonComponents/CustomToolTip';
import { MaximizeIcon, ElementTypeIcon, BadgeContent } from './Svgs';
import { SettingKey, getTogglesForType, TYPE_META } from './constants';
import './VisualizationControls.scss';

const formatKm = (m: number): string => (m / 1000).toFixed(3);
const formatDeg = (deg: number): string => deg.toFixed(2);

// ─── Component ──────────────────────────────────────────────────────
const VisualizationControls = () => {
    const {
        visualizationSettings, setVisualizationSettings, elementMap,
        selectedElement, setSelectedElement, activeTab,
        modals, replayState, visualizationData, viewer,
    } = useGenericSimulator();
    const { dashboard } = useUrlParams();
    const isSummaryLoading = dashboard === 'summary' && !visualizationData;
    const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>({});
    const [is3D, setIs3D] = useState(true);
    const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);

    const isConsoleOpen = modals.some(m => m.name === 'generic_simulator_console' && m.open);
    const needsSelection = activeTab === 'monitor' || isConsoleOpen;

    // ── Auto-select first element for monitor / console ─────────────
    useEffect(() => {
        if (!needsSelection) return;
        if (selectedElement) return;
        const allElements = Object.values(elementMap);
        if (allElements.length === 0) return;
        const sorted = [...allElements].sort((a, b) => a.elementName.localeCompare(b.elementName));
        const first = sorted[0];
        setSelectedElement({
            elementId: first.elementId,
            elementName: first.elementName,
            elementType: first.elementType,
        });
    }, [elementMap, selectedElement, setSelectedElement, needsSelection]);

    // ── Clear selection when returning to HUD (tab switch or console close) ──
    useEffect(() => {
        if (activeTab === 'hud' && !isConsoleOpen) {
            setSelectedElement(null);
        }
    }, [activeTab, isConsoleOpen]);

    // ── Live "updated X ago" for info panel ─────────────────────────
    const lastDataTsRef = useRef<number>(0);
    const [agoText, setAgoText] = useState<string>('');
    const selId = selectedElement?.elementId;
    const lastHistoryLenRef = useRef<number>(0);
    const isSummaryMode = !!visualizationData;

    useEffect(() => {
        if (!selId) { setAgoText(''); return; }
        if (isSummaryMode) {
            if (replayState.status === 'playing') setAgoText('Live');
            else if (replayState.status === 'paused') setAgoText('Paused');
            else setAgoText('');
        }
    }, [selId, isSummaryMode, replayState.status]);

    useEffect(() => {
        if (!selId) {
            lastDataTsRef.current = 0;
            lastHistoryLenRef.current = 0;
            setAgoText('');
        } else {
            const snap = getEcefHistorySnapshot();
            lastHistoryLenRef.current = snap[selId]?.length ?? 0;
            if (lastHistoryLenRef.current > 0) {
                lastDataTsRef.current = Date.now();
                setAgoText('Updated now');
            }
        }
    }, [selId]);

    useEffect(() => {
        if (!selId || isSummaryMode) return;
        const interval = setInterval(() => {
            const snap = getEcefHistorySnapshot();
            const currentLen = snap[selId]?.length ?? 0;
            if (currentLen > lastHistoryLenRef.current) {
                lastHistoryLenRef.current = currentLen;
                lastDataTsRef.current = Date.now();
                const latest = snap[selId][currentLen - 1];
                if (latest) {
                    const pos = { x: latest.pos_x, y: latest.pos_y, z: latest.pos_z };
                    const vel = { x: latest.vel_x, y: latest.vel_y, z: latest.vel_z };
                    const quat = { q1: latest.q_1, q2: latest.q_2, q3: latest.q_3, q4: latest.q_4 };
                    setSelectedElement(prev => {
                        if (!prev || prev.elementId !== selId) return prev;
                        return {
                            ...prev,
                            position: pos, velocity: vel, quaternion: quat,
                            altitude: computeAltitudeKm(pos),
                            speed: computeSpeedMs(vel),
                            eulerAngles: computeLVLHEulerAngles(pos, vel, quat) ?? prev.eulerAngles,
                        };
                    });
                }
            }
            const ts = lastDataTsRef.current;
            if (!ts) { setAgoText(''); return; }
            const diff = Math.floor((Date.now() - ts) / 1000);
            if (diff < 1) setAgoText('Updated just now');
            else if (diff < 60) setAgoText(`Updated ${diff}s ago`);
            else setAgoText(`Updated ${Math.floor(diff / 60)}m ago`);
        }, 1000);
        return () => clearInterval(interval);
    }, [selId, isSummaryMode, setSelectedElement]);

    // ── Select element ──────────────────────────────────────────────
    const onSelectElement = useCallback((el: ElementInfo) => {
        if (selectedElement?.elementId === el.elementId) {
            if (needsSelection) return;
            setSelectedElement(null);
            return;
        }
        const info: SelectedElementInfo = {
            elementId: el.elementId,
            elementName: el.elementName,
            elementType: el.elementType,
        };
        const historySnapshot = getEcefHistorySnapshot();
        const history = historySnapshot[el.elementId];
        const latest = history?.length ? history[history.length - 1] : null;
        if (latest) {
            const pos = { x: latest.pos_x, y: latest.pos_y, z: latest.pos_z };
            const vel = { x: latest.vel_x, y: latest.vel_y, z: latest.vel_z };
            const quat = { q1: latest.q_1, q2: latest.q_2, q3: latest.q_3, q4: latest.q_4 };
            info.position = pos;
            info.velocity = vel;
            info.quaternion = quat;
            info.altitude = computeAltitudeKm(pos);
            info.speed = computeSpeedMs(vel);
            const euler = computeLVLHEulerAngles(pos, vel, quat);
            if (euler) info.eulerAngles = euler;
        }
        setSelectedElement(info);
    }, [selectedElement, setSelectedElement, needsSelection]);

    // ── Toggle helpers ──────────────────────────────────────────────

    /** Toggle a setting for ALL elements of a given type (group header badge) */
    const toggleGroupType = useCallback((key: SettingKey, type: ElementType) => {
        setVisualizationSettings(prev => {
            const items = Object.values(elementMap).filter(e => e.elementType === type);
            const defaultVal = prev[key].globalEnabled;
            const allOn = items.every(e => {
                const v = prev[key].perElement[e.elementId];
                return v === undefined ? defaultVal : v;
            });
            const newPerElement = { ...prev[key].perElement };
            items.forEach(e => { newPerElement[e.elementId] = !allOn; });
            return {
                ...prev,
                [key]: { ...prev[key], perElement: newPerElement },
            };
        });
    }, [setVisualizationSettings, elementMap]);

    const togglePerElement = useCallback((key: SettingKey, elementId: string) => {
        setVisualizationSettings(prev => {
            const current = prev[key].perElement[elementId];
            const defaultVal = prev[key].globalEnabled;
            return {
                ...prev,
                [key]: {
                    ...prev[key],
                    perElement: { ...prev[key].perElement, [elementId]: current === undefined ? !defaultVal : !current },
                },
            };
        });
    }, [setVisualizationSettings]);

    const isPerElementOn = (key: SettingKey, id: string) => {
        const v = visualizationSettings[key].perElement[id];
        return v === undefined ? visualizationSettings[key].globalEnabled : v;
    };

    const toggleCollapse = (type: string) =>
        setCollapsedTypes(prev => ({ ...prev, [type]: !prev[type] }));

    // ── Grouped elements ────────────────────────────────────────────
    const grouped = useMemo(() => {
        const all = Object.values(elementMap);
        const groups: Record<string, ElementInfo[]> = {};
        all.forEach(e => {
            if (!groups[e.elementType]) groups[e.elementType] = [];
            groups[e.elementType].push(e);
        });
        Object.values(groups).forEach(g => g.sort((a, b) => a.elementName.localeCompare(b.elementName)));
        return groups;
    }, [elementMap]);

    // Count how many type groups actually have elements (for equal-height division)
    const activeGroupCount = useMemo(() =>
        TYPE_META.filter(({ type }) => grouped[type]?.length > 0).length,
    [grouped]);

    return (
        <div className="gs__elp">
            {isPanelCollapsed ? (
                <button
                    className="gs__elp__expand-btn"
                    onClick={() => setIsPanelCollapsed(false)}
                >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.5 1.5L7 5L3.5 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
            ) : (
            <>
            <div className={`gs__elp__list-panel ${isSummaryLoading ? 'gs__elp__list-panel--loading' : ''}`}>
                {/* ── ELEMENTS header ── */}
                <div className="gs__elp__header" onClick={() => setIsPanelCollapsed(true)}>
                    <span className="gs__elp__title">Elements ({Object.keys(elementMap).length})</span>
                    <div className="gs__elp__header-actions" onClick={e => e.stopPropagation()}>
                        <button
                            className="gs__elp__mode-btn"
                            onClick={() => {
                                if (!isViewerReady(viewer)) return;
                                if (is3D) {
                                    viewer.scene.morphTo2D(1.5);
                                } else {
                                    viewer.scene.morphTo3D(1.5);
                                }
                                setIs3D(prev => !prev);
                            }}
                        >
                            {is3D ? '2D' : '3D'}
                        </button>
                        <CustomToolTip title="Reset View" placement="top">
                            <button
                                className="gs__elp__header-icon"
                                onClick={() => {
                                    if (!isViewerReady(viewer)) return;
                                    viewer.scene.camera.flyTo({
                                        destination: Cesium.Cartesian3.fromDegrees(78.9629, 20.5937, 25000000),
                                        duration: 2,
                                    });
                                }}
                            >
                                <MaximizeIcon size={14} />
                            </button>
                        </CustomToolTip>
                    </div>
                </div>

                {/* ── Shimmer skeleton (shown while loading) ── */}
                {isSummaryLoading && (
                    <div className="gs__elp__shimmer-list">
                        {/* header-like bar */}
                        <div className="gs__elp__shimmer-row">
                            <div className="gs__elp__shimmer-bar" style={{ width: '45%' }} />
                        </div>
                        {/* element rows */}
                        {[80, 60, 72, 55].map((w, i) => (
                            <div className="gs__elp__shimmer-row" key={`s1-${i}`}>
                                <div className="gs__elp__shimmer-circle" />
                                <div className="gs__elp__shimmer-bar" style={{ width: `${w}%` }} />
                            </div>
                        ))}
                        {/* second group header */}
                        <div className="gs__elp__shimmer-row" style={{ marginTop: 6 }}>
                            <div className="gs__elp__shimmer-bar" style={{ width: '35%' }} />
                        </div>
                        {[65, 50, 75].map((w, i) => (
                            <div className="gs__elp__shimmer-row" key={`s2-${i}`}>
                                <div className="gs__elp__shimmer-circle" />
                                <div className="gs__elp__shimmer-bar" style={{ width: `${w}%` }} />
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Grouped list ── */}
                <div
                    className="gs__elp__groups"
                    style={{ '--group-count': activeGroupCount || 1 } as React.CSSProperties}
                >
                    {Object.keys(elementMap).length === 0 && (
                        <div className="gs__elp__empty">No elements found</div>
                    )}
                    {TYPE_META.map(({ type, fullLabel }) => {
                        const items = grouped[type];
                        if (!items || items.length === 0) return null;
                        const isCollapsed = !!collapsedTypes[type];
                        const togglesForType = getTogglesForType(type);

                        return (
                            <div key={type} className="gs__elp__group">
                                {/* ── Group header ── */}
                                <div className="gs__elp__group-header">
                                    <div className="gs__elp__group-info" onClick={() => toggleCollapse(type)}>
                                        <span className="gs__elp__group-label">{fullLabel}</span>
                                        <span className="gs__elp__group-count">({items.length})</span>
                                    </div>
                                    <div className="gs__elp__group-trailing">
                                        <div className="gs__elp__group-badges">
                                            {togglesForType.map(t => {
                                                const allOn = items.every(e => isPerElementOn(t.key, e.elementId));
                                                return (
                                                    <CustomToolTip key={t.key} title={t.label} placement="top">
                                                        <button
                                                            className={`gs__elp__badge ${allOn ? 'on' : ''}`}
                                                            onClick={() => toggleGroupType(t.key, type)}
                                                        >
                                                            <BadgeContent short={t.short} />
                                                        </button>
                                                    </CustomToolTip>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Element rows ── */}
                                {!isCollapsed && (
                                    <div className="gs__elp__el-list">
                                        {items.map(el => {
                                            const isSel = selectedElement?.elementId === el.elementId;
                                            const elToggles = getTogglesForType(el.elementType);

                                            return (
                                                <div
                                                    key={el.elementId}
                                                    className={`gs__elp__el ${isSel ? 'gs__elp__el--sel' : ''}`}
                                                    onClick={() => onSelectElement(el)}
                                                >
                                                    {/* Leading: icon + name */}
                                                    <CustomToolTip title={el.elementName} placement="top">
                                                        <div className="gs__elp__el-leading">
                                                            <span className="gs__elp__el-icon">
                                                                <ElementTypeIcon type={el.elementType} size={16} />
                                                            </span>
                                                            <span className="gs__elp__el-text">
                                                                {truncateName(el.elementName, 14, 14, 0)}
                                                            </span>
                                                        </div>
                                                    </CustomToolTip>

                                                    {/* Trailing: all toggle badges as text */}
                                                    <div className="gs__elp__el-trailing">
                                                        <div className="gs__elp__el-badges">
                                                            {elToggles.map(t => {
                                                                const on = isPerElementOn(t.key, el.elementId);
                                                                return (
                                                                    <CustomToolTip key={t.key} title={t.label} placement="top">
                                                                        <button
                                                                            className={`gs__elp__badge ${on ? 'on' : ''}`}
                                                                            onClick={e => { e.stopPropagation(); togglePerElement(t.key, el.elementId); }}
                                                                        >
                                                                            <BadgeContent short={t.short} />
                                                                        </button>
                                                                    </CustomToolTip>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Info Panel (shows when element selected, only on HUD without console) ── */}
            {activeTab === 'hud' && !isConsoleOpen && selectedElement && (() => {
                const typeMeta = TYPE_META.find(t => t.type === selectedElement.elementType);
                const pipColor = typeMeta?.color ?? '#888';
                return (
                    <div className="gs__elp__info-panel">
                        {/* ── Header ── */}
                        <div className="gs__elp__info-header">
                            <div className="gs__elp__info-header-left">
                                <div className="gs__elp__info-title-row">
                                    <span className="gs__elp__info-pip" style={{ background: pipColor }} />
                                    <span className="gs__elp__info-name">{selectedElement.elementName}</span>
                                </div>
                                {agoText && (
                                    <span className="gs__elp__info-ago">{agoText}</span>
                                )}
                            </div>
                            <button className="gs__elp__info-close" onClick={() => setSelectedElement(null)}>✕</button>
                        </div>

                        {/* ── Position - ECEF ── */}
                        {selectedElement.position && (
                            <div className="gs__elp__info-section">
                                <div className="gs__elp__info-section-title">Position - ECEF</div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">X</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatKm(selectedElement.position.x)}</span>
                                        <span className="gs__elp__info-unit">km</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Y</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatKm(selectedElement.position.y)}</span>
                                        <span className="gs__elp__info-unit">km</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Z</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatKm(selectedElement.position.z)}</span>
                                        <span className="gs__elp__info-unit">km</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Velocity - ECEF (hidden for ground stations) ── */}
                        {selectedElement.velocity && selectedElement.elementType !== 'groundstation' && (
                            <div className="gs__elp__info-section">
                                <div className="gs__elp__info-section-title">Velocity - ECEF</div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Vx</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{selectedElement.velocity.x.toFixed(2)}</span>
                                        <span className="gs__elp__info-unit">m/s</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Vy</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{selectedElement.velocity.y.toFixed(2)}</span>
                                        <span className="gs__elp__info-unit">m/s</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Vz</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{selectedElement.velocity.z.toFixed(2)}</span>
                                        <span className="gs__elp__info-unit">m/s</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Attitude ── */}
                        {selectedElement.eulerAngles && (
                            <div className="gs__elp__info-section">
                                <div className="gs__elp__info-section-title">Attitude - LVLH</div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Roll</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatDeg(selectedElement.eulerAngles.roll)}</span>
                                        <span className="gs__elp__info-unit">°</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Pitch</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatDeg(selectedElement.eulerAngles.pitch)}</span>
                                        <span className="gs__elp__info-unit">°</span>
                                    </div>
                                </div>
                                <div className="gs__elp__info-row">
                                    <span className="gs__elp__info-key">Yaw</span>
                                    <div className="gs__elp__info-value-group">
                                        <span className="gs__elp__info-val">{formatDeg(selectedElement.eulerAngles.yaw)}</span>
                                        <span className="gs__elp__info-unit">°</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}
            </>
            )}
        </div>
    );
};

export default VisualizationControls;
